import { EditOutlined, GoogleUniversal, GroupOrderUniversal } from '@duxton-web/icons';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Descriptions,
  Dropdown,
  Flex,
  Modal,
  Spin,
  Tag,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'grab-ui';

import { getFeatureFlagConfig } from 'actions/newTridentExtra';
import {
  APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC,
  applyJourneyCampaignSheetSync,
  GET_JOURNEY_CAMPAIGN_GROUP,
  getJourneyCampaignGroup,
  previewJourneyCampaignSheetSync,
  resetJourneyCampaignGroup,
  UPDATE_UCD_CAMPAIGN_STATUS,
  updateUCDCampaignStatus,
  updateUCDCampaignStatuses,
} from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import {
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { UcdCampaignResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';
import type {
  JourneyCampaignGroupCampaign,
  JourneyCampaignGroupResponse,
} from 'omega-rtc/types/ts/journey-campaign-group';
import { useJourneyCampaignGroupEnabled } from 'omega-rtc/utils/portal-config';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import {
  getValueByFeatureFlag,
  hasPermission,
  hasTridentPermission,
} from 'omega-rtc/utils/user-utils';
import { getCountryFlag } from 'omega-rtc/views/ucd/UCDCreateJourneyCampaign';
import {
  getJourneyCampaignProductOfferingLabel,
  getJourneyCampaignVerticalLabel,
} from 'omega-rtc/views/ucd/JourneyCampaignMetadataOptions';
import { RTC_NAMESPACE } from 'namespace';
import NotFoundPage from 'views/not-found/NotFoundPage';

// @ts-ignore
import aiLoading from 'img/ai_loading.gif';

import './JourneyCampaignGroupViewPage.scss';

type Props = {
  params: { campaignGroupID?: string };
  router: { push: (path: string) => void };
};

type ViewProps = {
  campaignGroup: JourneyCampaignGroupResponse;
  hasActivePermission: boolean;
  hasUpdatePermission: boolean;
  onCountryAction: (campaign: JourneyCampaignGroupCampaign, action: string) => void;
  onBulkCountryAction: (
    campaigns: JourneyCampaignGroupCampaign[],
    action: string,
  ) => void;
  onEdit?: () => void;
  onViewCountry: (campaign: JourneyCampaignGroupCampaign) => void;
};

const EMPTY_CAMPAIGNS: JourneyCampaignGroupCampaign[] = [];
const EMPTY_CAMPAIGN_IDS: string[] = [];

export const getJourneyCampaignAudienceLabel = (audienceType = '') => ({
  DAX: 'Driver',
  MEX: 'Merchant',
  PAX: 'Passenger',
}[audienceType.toUpperCase()] || formatJourneyCampaignStatus(audienceType));

export const getJourneyTypeLabel = (journeyType = '') => (
  formatJourneyCampaignStatus(journeyType)
);

export const normalizeJourneyCampaignStatus = (status = '') => status
  .trim()
  .toUpperCase()
  .replace(/\s+/g, '_');

export const formatJourneyCampaignStatus = (status = '') => normalizeJourneyCampaignStatus(status)
  .split('_')
  .filter(Boolean)
  .map((part) => `${part.charAt(0)}${part.slice(1).toLowerCase()}`)
  .join(' ');

export const getJourneyCampaignStatusColor = (status: string) => {
  const normalizedStatus = normalizeJourneyCampaignStatus(status);
  if (normalizedStatus === 'ACTIVE') return 'success';
  if (normalizedStatus === 'PENDING_APPROVAL') return 'processing';
  if (normalizedStatus === 'DRAFT') return 'warning';
  return 'default';
};

export const getJourneyCampaignStatusClass = (status: string) => {
  const normalizedStatus = normalizeJourneyCampaignStatus(status);
  if (normalizedStatus === 'ACTIVE') return 'journey-campaign-status-active';
  if (normalizedStatus === 'PENDING_APPROVAL') {
    return 'journey-campaign-status-pending-approval';
  }
  if (normalizedStatus === 'DRAFT') return 'journey-campaign-status-draft';
  return 'journey-campaign-status-default';
};

export const getJourneyCampaignGroupStatus = (
  campaigns: JourneyCampaignGroupCampaign[],
) => {
  const statuses = Array.from(new Set(campaigns.map(
    (campaign) => normalizeJourneyCampaignStatus(campaign.status),
  )));
  return statuses.length === 1 ? statuses[0] : '';
};

export const getJourneyCampaignGroupCountryActions = (
  campaign: JourneyCampaignGroupCampaign,
  canSubmitForApproval: boolean,
  hasUpdatePermission: boolean,
  hasActivePermission: boolean,
) => {
  const metadata = campaign.journeyMetadata || {};
  if (normalizeJourneyCampaignStatus(metadata.syncStatus) === 'IN_PROGRESS') {
    return [];
  }

  const status = normalizeJourneyCampaignStatus(campaign.status);
  if (status === 'DRAFT' && hasUpdatePermission) {
    const actions = canSubmitForApproval
      ? [{ key: 'submit', label: 'Submit for approval' }]
      : [];
    const sourceUpdatedAt = metadata.source && metadata.source.lastUpdatedAt;
    const hasPendingSheetUpdates = !metadata.lastSyncedAt
      || (sourceUpdatedAt && dayjs(sourceUpdatedAt).isAfter(
        dayjs(metadata.lastSyncedAt),
      ));
    if (hasPendingSheetUpdates) {
      actions.push({ key: 'preview-sync', label: 'Sync from Sheet' });
    }
    return actions;
  }
  if (status === 'PENDING_APPROVAL' && hasActivePermission) {
    return [
      { key: 'approve', label: 'Approve' },
      { key: 'reject', label: 'Reject' },
    ];
  }
  if (status === 'ACTIVE' && hasActivePermission) {
    return [{ key: 'deactivate', label: 'Deactivate' }];
  }
  return [];
};

export const getCommonJourneyCampaignGroupActions = (
  campaigns: JourneyCampaignGroupCampaign[],
  canSubmitForApproval: boolean,
  hasUpdatePermission: boolean,
  hasActivePermission: boolean,
) => {
  if (!campaigns.length || new Set(campaigns.map(
    (campaign) => normalizeJourneyCampaignStatus(campaign.status),
  )).size > 1) {
    return [];
  }

  const [firstCampaign, ...remainingCampaigns] = campaigns;
  const remainingActionKeys = remainingCampaigns.map((campaign) => new Set(
    getJourneyCampaignGroupCountryActions(
      campaign,
      canSubmitForApproval,
      hasUpdatePermission,
      hasActivePermission,
    ).map((action) => action.key),
  ));
  return getJourneyCampaignGroupCountryActions(
    firstCampaign,
    canSubmitForApproval,
    hasUpdatePermission,
    hasActivePermission,
  ).filter((action) => remainingActionKeys.every((keys) => keys.has(action.key)));
};

const formatDate = (value?: string) => (
  value && dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '—'
);

const formatSyncTime = (value?: string) => (
  value && dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY, HH:mm Z') : '—'
);

const getSafeHttpUrl = (value?: string) => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
  } catch (_) {
    return undefined;
  }
};

export const getJourneyCampaignSyncStatus = (
  campaign: JourneyCampaignGroupCampaign,
) => {
  const metadata = campaign.journeyMetadata || {};
  if (normalizeJourneyCampaignStatus(metadata.syncStatus) === 'IN_PROGRESS') {
    return { color: 'processing', label: 'Sync in progress', loading: true };
  }
  if (!metadata.lastSyncedAt) return undefined;
  if (metadata.source && metadata.source.lastUpdatedAt
    && dayjs(metadata.source.lastUpdatedAt).isAfter(dayjs(metadata.lastSyncedAt))) {
    return { color: 'warning', label: 'Newer sheet version available', loading: false };
  }
  return { color: 'success', label: 'Synced', loading: false };
};

const CampaignOverview = ({ campaignGroup }: {
  campaignGroup: JourneyCampaignGroupResponse;
}) => (
  <Card
    className={`journey-campaign-overview ${getJourneyCampaignStatusClass(
      getJourneyCampaignGroupStatus(campaignGroup.campaigns || EMPTY_CAMPAIGNS),
    )}`}
    style={{ marginBottom: '16px', maxWidth: '1316px' }}
    title={(
      <Flex vertical gap="2px">
        <Typography.Title level={4} style={{ margin: 0 }}>
          Campaign overview
        </Typography.Title>
        <Typography.Text type="secondary">
          Audience, journey setup and campaign schedule
        </Typography.Text>
      </Flex>
    )}
  >
    <div className="journey-campaign-overview-grid">
      <div className="journey-campaign-overview-item">
        <Typography.Text type="secondary">Audience type</Typography.Text>
        <Typography.Text strong>
          {getJourneyCampaignAudienceLabel(campaignGroup.audienceType) || '—'}
        </Typography.Text>
      </div>
      <div className="journey-campaign-overview-item">
        <Typography.Text type="secondary">Journey type</Typography.Text>
        <Typography.Text strong>
          {getJourneyTypeLabel(campaignGroup.journeyType) || '—'}
        </Typography.Text>
      </div>
      <div className="journey-campaign-overview-item">
        <Typography.Text type="secondary">Vertical</Typography.Text>
        <Typography.Text strong>
          {getJourneyCampaignVerticalLabel(campaignGroup.vertical) || '—'}
        </Typography.Text>
      </div>
      <div className="journey-campaign-overview-item">
        <Typography.Text type="secondary">Product offering</Typography.Text>
        <Typography.Text strong>
          {getJourneyCampaignProductOfferingLabel(campaignGroup.productOffering) || '—'}
        </Typography.Text>
      </div>
      <div className="journey-campaign-overview-item journey-campaign-overview-period">
        <Typography.Text type="secondary">Campaign period</Typography.Text>
        <div className="journey-campaign-overview-dates">
          <div>
            <Typography.Text type="secondary">Start date</Typography.Text>
            <Typography.Text strong>{formatDate(campaignGroup.startDate)}</Typography.Text>
          </div>
          <span aria-hidden="true" className="journey-campaign-overview-arrow">&rarr;</span>
          <div>
            <Typography.Text type="secondary">End date</Typography.Text>
            <Typography.Text strong>{formatDate(campaignGroup.endDate)}</Typography.Text>
          </div>
        </div>
      </div>
    </div>
  </Card>
);

const CountryCard = ({
  campaign,
  campaignGroupName,
  hasActivePermission,
  hasUpdatePermission,
  isSelectable,
  isSelected,
  onCountryAction,
  onSelect,
  onViewCountry,
}: {
  campaign: JourneyCampaignGroupCampaign;
  campaignGroupName: string;
  hasActivePermission: boolean;
  hasUpdatePermission: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  onCountryAction: (campaign: JourneyCampaignGroupCampaign, action: string) => void;
  onSelect: (campaignID: string, selected: boolean) => void;
  onViewCountry: (campaign: JourneyCampaignGroupCampaign) => void;
}) => {
  const metadata = campaign.journeyMetadata || {};
  const sourceURL = getSafeHttpUrl(metadata.source && metadata.source.url);
  const actions = getJourneyCampaignGroupCountryActions(
    campaign,
    !!metadata.lastSyncedAt,
    hasUpdatePermission,
    hasActivePermission,
  );
  const groups = metadata.groups || [];
  const status = formatJourneyCampaignStatus(campaign.status) || 'Unknown';
  const syncStatus = getJourneyCampaignSyncStatus(campaign);

  return (
    <Card
      className={[
        'journey-campaign-country-card',
        getJourneyCampaignStatusClass(campaign.status),
        isSelected ? 'journey-campaign-country-card-selected' : '',
      ].filter(Boolean).join(' ')}
      style={{ width: '100%' }}
      title={(
        <Flex align="center" gap="8px" wrap="wrap">
          {isSelectable && (
            <Checkbox
              aria-label={`Select ${campaign.countryName || campaign.countryCode}`}
              checked={isSelected}
              onChange={(event) => onSelect(campaign.campaignID, event.target.checked)}
            />
          )}
          <span aria-hidden="true" className="journey-campaign-country-flag">
            {getCountryFlag(campaign.countryCode || '')}
          </span>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {campaign.countryName || campaign.countryCode || `Country ${campaign.countryID}`}
          </Typography.Title>
        </Flex>
      )}
      extra={(
        <Flex align="center" gap="8px">
          <Tag
            className="journey-campaign-country-status"
            color={getJourneyCampaignStatusColor(campaign.status)}
          >
            {status}
          </Tag>
          <Dropdown
            menu={{
              items: actions,
              onClick: ({ key }) => onCountryAction(campaign, key),
            }}
            trigger={['click']}
          >
            <Button disabled={!actions.length}>Action</Button>
          </Dropdown>
        </Flex>
      )}
    >
      <Descriptions column={1}>
        <Descriptions.Item label="Campaign Name">
          <a
            href={UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_VIEW(
              campaign.projectID,
              campaign.campaignID,
            )}
            onClick={(event) => {
              event.preventDefault();
              onViewCountry(campaign);
            }}
          >
            {campaign.name || `${campaignGroupName} - ${campaign.countryCode || campaign.countryID}`}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Source">
          {sourceURL ? (
            <a href={sourceURL} rel="noreferrer" target="_blank">
              <Flex align="center" gap="6px">
                <GoogleUniversal aria-hidden="true" />
                <span>Google Sheet</span>
              </Flex>
            </a>
          ) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Last synced">
          {metadata.lastSyncedAt ? formatSyncTime(metadata.lastSyncedAt) : 'No sync history'}
        </Descriptions.Item>
        <Descriptions.Item label="Last Google Sheet updated">
          {formatSyncTime(metadata.source && metadata.source.lastUpdatedAt)}
        </Descriptions.Item>
        {syncStatus && (
          <Descriptions.Item label="Sync status">
            <Tag className="journey-campaign-sync-progress-tag" color={syncStatus.color}>
              <Flex align="center" gap="6px">
                {syncStatus.loading && (
                  <img alt="" aria-hidden="true" height={16} src={aiLoading} width={16} />
                )}
                <span>{syncStatus.label}</span>
              </Flex>
            </Tag>
          </Descriptions.Item>
        )}
      </Descriptions>
      {metadata.lastSyncedAt && (
        <div className="journey-campaign-country-groups">
          <Collapse
            bordered={false}
            className="journey-campaign-country-groups-collapse"
            defaultActiveKey={[]}
            expandIconPosition="end"
            items={[{
              key: 'group-ids',
              label: (
                <Flex align="center" gap="8px">
                  <Typography.Text strong>Group IDs</Typography.Text>
                  <Tag>{groups.length}</Tag>
                </Flex>
              ),
              children: groups.length ? (
                <Flex gap="8px" wrap="wrap">
                  {groups.map((group) => (
                    <div className="journey-campaign-country-group" key={group.groupID}>
                      <GroupOrderUniversal aria-hidden="true" />
                      <Typography.Text strong>{group.groupID}</Typography.Text>
                      <Tag>
                        {group.numberOfVariants}{' '}
                        {group.numberOfVariants === 1 ? 'variant' : 'variants'}
                      </Tag>
                    </div>
                  ))}
                </Flex>
              ) : <Typography.Text type="secondary">No group IDs configured.</Typography.Text>,
            }]}
          />
        </div>
      )}
    </Card>
  );
};

export const JourneyCampaignGroupView = ({
  campaignGroup,
  hasActivePermission,
  hasUpdatePermission,
  onBulkCountryAction,
  onCountryAction,
  onEdit,
  onViewCountry,
}: ViewProps) => {
  const campaigns = campaignGroup.campaigns || EMPTY_CAMPAIGNS;
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedCampaignIDs, setSelectedCampaignIDs] = useState(EMPTY_CAMPAIGN_IDS);
  const selectedCampaigns = campaigns.filter(
    (campaign) => selectedCampaignIDs.includes(campaign.campaignID),
  );
  const hasMixedStatuses = new Set(selectedCampaigns.map(
    (campaign) => normalizeJourneyCampaignStatus(campaign.status),
  )).size > 1;
  const canSubmitSelectedForApproval = selectedCampaigns.every(
    (campaign) => !!(campaign.journeyMetadata && campaign.journeyMetadata.lastSyncedAt),
  );
  const commonActions = getCommonJourneyCampaignGroupActions(
    selectedCampaigns,
    canSubmitSelectedForApproval,
    hasUpdatePermission,
    hasActivePermission,
  );
  const areAllSelected = !!campaigns.length
    && selectedCampaigns.length === campaigns.length;

  const toggleSelection = () => {
    if (isSelecting) setSelectedCampaignIDs([]);
    setIsSelecting(!isSelecting);
  };

  return (
    <DocumentTitle title={`${campaignGroup.name} - Holos`}>
      <div className={`ucd-basic journey-campaign-group-page ${
        selectedCampaigns.length ? 'journey-campaign-view-selecting' : ''
      }`}
      >
        <Flex
          align="center"
          gap="12px"
          justify="space-between"
          style={{ marginBottom: '24px', maxWidth: '1316px' }}
          wrap="wrap"
        >
          <Typography.Title level={2} style={{ margin: 0 }}>
            {campaignGroup.name}
          </Typography.Title>
          <Flex align="center" gap="16px">
            {isSelecting && (
              <Checkbox
                aria-label="Select all countries"
                checked={areAllSelected}
                indeterminate={!!selectedCampaigns.length && !areAllSelected}
                onChange={(event) => setSelectedCampaignIDs(event.target.checked
                  ? campaigns.map((campaign) => campaign.campaignID)
                  : [])}
              >
                Select all
              </Checkbox>
            )}
            <Flex gap="8px">
              <Button onClick={toggleSelection} type={isSelecting ? 'primary' : 'default'}>
                {isSelecting ? 'Done' : 'Select'}
              </Button>
              {hasUpdatePermission && (
                <Button
                  aria-label="Edit"
                  disabled={!onEdit}
                  icon={<EditOutlined />}
                  onClick={onEdit}
                >
                  Edit
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
        <CampaignOverview campaignGroup={campaignGroup} />
        <div className="journey-campaign-country-grid">
          {campaigns.map((campaign) => (
            <CountryCard
              key={campaign.campaignID}
              campaign={campaign}
              campaignGroupName={campaignGroup.name}
              hasActivePermission={hasActivePermission}
              hasUpdatePermission={hasUpdatePermission}
              isSelectable={isSelecting}
              isSelected={selectedCampaignIDs.includes(campaign.campaignID)}
              onCountryAction={onCountryAction}
              onSelect={(campaignID, selected) => setSelectedCampaignIDs((current) => (
                selected
                  ? Array.from(new Set([...current, campaignID]))
                  : current.filter((ID) => ID !== campaignID)
              ))}
              onViewCountry={onViewCountry}
            />
          ))}
        </div>
        {!!selectedCampaigns.length && (
          <div className="journey-campaign-selection-bar">
            <Typography.Text strong>
              {selectedCampaigns.length}{' '}
              {selectedCampaigns.length === 1 ? 'country' : 'countries'} selected
            </Typography.Text>
            {hasMixedStatuses ? (
              <Typography.Text type="warning">
                Select only campaigns with the same status to perform an action.
              </Typography.Text>
            ) : (
              <Flex gap="8px" wrap="wrap">
                {commonActions.map((action) => (
                  <Button
                    key={action.key}
                    onClick={() => onBulkCountryAction(selectedCampaigns, action.key)}
                  >
                    {action.label}
                  </Button>
                ))}
              </Flex>
            )}
          </div>
        )}
      </div>
    </DocumentTitle>
  );
};

const JourneyCampaignGroupViewPage = ({ params, router }: Props) => {
  const dispatch = useDispatch();
  const enabled = useJourneyCampaignGroupEnabled();
  const campaignGroupID = params.campaignGroupID || '';
  const campaignGroup = useSelector(
    (state: any) => state.ucd.journeyCampaignGroupView,
  );
  const request = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(GET_JOURNEY_CAMPAIGN_GROUP),
    {},
  ));
  const applySheetSyncRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC),
    {},
  ));
  const updateStatusRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(UPDATE_UCD_CAMPAIGN_STATUS),
    {},
  ));
  const user = useSelector((state: any) => state.app.user) || {};
  const featureFlagConfig = useSelector(
    (state: any) => _.get(state, 'newTridents.featureFlagConfig', {}),
  );
  const updatedCampaignStatusTimestamp = useSelector(
    (state: any) => state.ucd.updatedCampaignStatusTimestamp,
  );
  const sheetSyncPreview = useSelector(
    (state: any) => state.ucd.journeyCampaignSheetSyncPreview,
  );
  const [syncCampaigns, setSyncCampaigns] = useState(EMPTY_CAMPAIGNS);
  const [hasAttemptedApply, setHasAttemptedApply] = useState(false);
  const [bulkCampaignCount, setBulkCampaignCount] = useState(0);

  const hasUpdatePermission = useMemo(() => getValueByFeatureFlag(
    _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false),
    hasPermission(user.userPermissions || {}, UcdCampaignResourceName, UcdUniversalScope, 'update'),
    hasTridentPermission(user.userPermissions || {}, '', '', 'update'),
  ), [featureFlagConfig, user.userPermissions]);
  const hasActivePermission = useMemo(() => getValueByFeatureFlag(
    _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false),
    hasPermission(user.userPermissions || {}, UcdCampaignResourceName, UcdUniversalScope, 'active'),
    hasTridentPermission(user.userPermissions || {}, '', '', 'active'),
  ), [featureFlagConfig, user.userPermissions]);

  useEffect(() => {
    dispatch(getFeatureFlagConfig());
  }, [dispatch]);

  useEffect(() => {
    if (enabled && campaignGroupID) dispatch(getJourneyCampaignGroup(campaignGroupID));
    return () => { dispatch(resetJourneyCampaignGroup()); };
  }, [campaignGroupID, dispatch, enabled, updatedCampaignStatusTimestamp]);

  useEffect(() => {
    if (hasAttemptedApply && applySheetSyncRequest.isSuccessful && syncCampaigns.length) {
      setHasAttemptedApply(false);
      setSyncCampaigns([]);
      dispatch(getJourneyCampaignGroup(campaignGroupID));
    }
  }, [
    applySheetSyncRequest.isSuccessful,
    campaignGroupID,
    dispatch,
    hasAttemptedApply,
    syncCampaigns.length,
  ]);

  useEffect(() => {
    if (!bulkCampaignCount || updateStatusRequest.isPending) return;
    if (updateStatusRequest.isSuccessful) {
      snackBar.success({
        content: `${bulkCampaignCount} campaign${bulkCampaignCount === 1 ? '' : 's'} updated successfully`,
      });
      setBulkCampaignCount(0);
    } else if (updateStatusRequest.isFailure) {
      snackBar.error({
        content: `Bulk update did not complete for all ${bulkCampaignCount} campaign${bulkCampaignCount === 1 ? '' : 's'}. Campaign data has been refreshed.`,
      });
      setBulkCampaignCount(0);
      dispatch(getJourneyCampaignGroup(campaignGroupID));
    }
  }, [bulkCampaignCount, campaignGroupID, dispatch, updateStatusRequest]);

  if (!enabled) {
    return <NotFoundPage namespace={RTC_NAMESPACE} />;
  }

  const isLoaded = campaignGroup && campaignGroup.ID === campaignGroupID;
  if (request.isFailure) {
    return <Alert message="Journey Campaign Group could not be loaded" showIcon type="error" />;
  }
  if (isRequestsPending(request) || !isLoaded) {
    return <Flex align="center" justify="center" style={{ minHeight: 320 }}><Spin /></Flex>;
  }

  const handleCountryAction = (
    campaign: JourneyCampaignGroupCampaign,
    action: string,
  ) => {
    if (action === 'preview-sync') {
      setHasAttemptedApply(false);
      setSyncCampaigns([campaign]);
      dispatch(previewJourneyCampaignSheetSync(campaignGroupID, campaign));
      return;
    }
    const statusByAction: Record<string, string> = {
      approve: 'ACTIVE',
      deactivate: 'DEACTIVATED',
      reject: 'DRAFT',
      submit: 'PENDING_APPROVAL',
    };
    if (statusByAction[action]) {
      dispatch(updateUCDCampaignStatus(
        statusByAction[action],
        user.email,
        campaign.projectID,
        campaign.campaignID,
      ));
    }
  };

  const handleBulkCountryAction = (
    campaigns: JourneyCampaignGroupCampaign[],
    action: string,
  ) => {
    if (action === 'preview-sync') {
      setHasAttemptedApply(false);
      setSyncCampaigns(campaigns);
      dispatch(previewJourneyCampaignSheetSync(campaignGroupID, campaigns));
      return;
    }
    const statusByAction: Record<string, string> = {
      approve: 'ACTIVE',
      deactivate: 'DEACTIVATED',
      reject: 'DRAFT',
      submit: 'PENDING_APPROVAL',
    };
    if (statusByAction[action]) {
      setBulkCampaignCount(campaigns.length);
      dispatch(updateUCDCampaignStatuses(statusByAction[action], user.email, campaigns));
    }
  };

  const closeSyncModal = () => {
    setHasAttemptedApply(false);
    setSyncCampaigns([]);
  };

  return (
    <React.Fragment>
      <JourneyCampaignGroupView
        campaignGroup={campaignGroup}
        hasActivePermission={hasActivePermission}
        hasUpdatePermission={hasUpdatePermission}
        onBulkCountryAction={handleBulkCountryAction}
        onCountryAction={handleCountryAction}
        onEdit={() => router.push(UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_GROUP_EDIT(
          campaignGroupID,
        ))}
        onViewCountry={(campaign) => router.push(UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_VIEW(
          campaign.projectID,
          campaign.campaignID,
        ))}
      />
      <Modal
        footer={(
          <Flex justify="end" gap="8px">
            <Button onClick={closeSyncModal}>Cancel</Button>
            <Button
              disabled={!sheetSyncPreview || !sheetSyncPreview.previewToken
                || applySheetSyncRequest.isPending}
              loading={applySheetSyncRequest.isPending}
              type="primary"
              onClick={() => {
                setHasAttemptedApply(true);
                dispatch(applyJourneyCampaignSheetSync(
                  campaignGroupID,
                  sheetSyncPreview.previewToken,
                  syncCampaigns,
                ));
              }}
            >
              Apply update
            </Button>
          </Flex>
        )}
        onCancel={closeSyncModal}
        open={!!syncCampaigns.length}
        title="Review Sheet Update"
      >
        <Typography.Paragraph>
          {sheetSyncPreview && sheetSyncPreview.previewToken
            ? 'Review the detected Google Sheet changes before applying them to this country campaign.'
            : 'Loading Google Sheet changes…'}
        </Typography.Paragraph>
        {hasAttemptedApply && applySheetSyncRequest.isFailure && (
          <Alert
            message="Unable to apply the Google Sheet update. Please try again."
            showIcon
            type="error"
          />
        )}
      </Modal>
    </React.Fragment>
  );
};

const AuthorizedJourneyCampaignGroupViewPage = (props: Props) => (
  <ShowIfAuthorized
    conditions={[{ resource: UCD_RESOURCE_NAMES.campaign, action: UCD_ACTION_TYPES.read }]}
    unauthorizedContent={(
      <Alert message="You don't have access to Holos campaign" showIcon type="error" />
    )}
  >
    <JourneyCampaignGroupViewPage {...props} />
  </ShowIfAuthorized>
);

export default AuthorizedJourneyCampaignGroupViewPage;
